import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Box,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  Text,
  columnTypes,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  usePortal,
  useSecuritiesContext,
  useWSFilterPipe,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { GetContextMenuItems, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import { map } from 'rxjs';
import { colIDToFilterBuilderKey, usePriceBlotterFilter } from './usePriceBlotterFilter';

import { compact } from 'lodash-es';
import { PriceBlotterEntity } from './PriceBlotterEntity';
import { DEFAULT_PRICE_BLOTTER_COLUMNS, DEFAULT_PRICE_BLOTTER_FILTER, PRICE_BLOTTER_PORTAL_ID } from './defaults';
import type { PriceBlotterTabProps } from './types';
import { usePriceBlotterColumns } from './usePriceBlotterColumns';
import { usePriceBlotterMarketDataEnrichment } from './usePriceBlotterMarketDataEnrichment';

function getUniqueKey(pbe: PriceBlotterEntity) {
  return pbe.security.Symbol;
}

export interface PriceBlotterParams extends PriceBlotterTabProps {
  blotterID: string;
  onUpdate?: (tab: PriceBlotterTabProps) => void;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  tag?: string;
}

export function PriceBlotter({
  blotterID,
  onUpdate,
  initialIsOpen,
  onCloneTab,
  tag,
  defaultColumns = DEFAULT_PRICE_BLOTTER_COLUMNS,
  defaultFilter = DEFAULT_PRICE_BLOTTER_FILTER,
}: PriceBlotterParams) {
  const mixpanel = useMixpanel();
  const { enablePriceGridColumnDynamics } = useFeatureFlag();
  const { securitiesBySymbolObs } = useSecuritiesContext();
  const { setPortalRef: filtersPortalRef } = usePortal(PRICE_BLOTTER_PORTAL_ID);

  // We never clean up the persisted state when a tab is removed
  // We should store this in the panel params when using flexible layout
  const persistence = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const { columns, columnDynamicsSpecs, dialogs } = usePriceBlotterColumns(
    defaultColumns,
    enablePriceGridColumnDynamics
  );

  const filterResults = usePriceBlotterFilter({
    persistedBlotterTable: persistence,
  });

  const filterPipe = useWSFilterPipe({ getUniqueKey, filterFunc: filterResults.clientSideFilter });
  const dataObservable = useMemo(
    () =>
      securitiesBySymbolObs.pipe(
        map(securitiesBySymbol => {
          return { initial: true, data: [...securitiesBySymbol.values()].map(sec => new PriceBlotterEntity(sec)) };
        }),
        filterPipe
      ),
    [securitiesBySymbolObs, filterPipe]
  );

  const { filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { jsonModal, handleClickJson } = useJsonModal();

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems: GetContextMenuItems<PriceBlotterEntity> = useCallbackRef(
    (params: GetContextMenuItemsParams) => {
      const items: ('separator' | MenuItemDef)[] = getDefaultContextMenuItems(params);

      items.unshift(
        ...filterByCellValueMenuItem({
          params,
          filterableProperties: filterResults.filterBuilderProps.properties,
          openClause: filterBuilderAccordion.openClause,
          colIDToFilterBuilderKey,
          mixpanel,
        })
      );

      const data = params.node?.data;
      if (data) {
        items.unshift('separator');
        items.unshift(getShowJSONContextItem({ params, handleClickJson }));
      }

      return compact(items);
    }
  );

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const marketDataEnrichment = usePriceBlotterMarketDataEnrichment({ dataObservable });

  const blotterTable = useBlotterTable<PriceBlotterEntity>({
    rowID: 'rowID' satisfies keyof PriceBlotterEntity,
    dataObservable: marketDataEnrichment.enrichedDataObservable,
    errorObservable: undefined,
    columns,
    persistence,
    ...(enablePriceGridColumnDynamics
      ? {
          columnDynamicsParams: {
            columnDynamicsSpecs,
          },
        }
      : {}),
    gridOptions: {
      ...marketDataEnrichment.gridOptionsOverlay,
      groupRemoveSingleChildren: true,
      showOpenedGroup: true,
      autoGroupColumnDef,
      rowBuffer: 0, // we dont render any rows apart from the ones that are visible
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      groupDisplayType: 'singleColumn',
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <HStack justifyContent="space-between" h="40px" px="spacing12" borderBottom="1px solid var(--backgroundDivider)">
        <Text color="colorTextImportant" fontWeight={500}>
          Price Grid
        </Text>
        <Box ref={filtersPortalRef} />
      </HStack>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        portalId={PRICE_BLOTTER_PORTAL_ID}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        /* Only show the menu and clone tab button if we know how to handle it */
        suffix={
          onCloneTab && (
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <>
                <Divider orientation="horizontal" />
                <Button
                  startIcon={IconName.Duplicate}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={handleCloneTab}
                >
                  Clone Tab
                </Button>
              </>
            </BlotterTableExtrasMenu>
          )
        }
      />
      <BlotterTable {...blotterTable} />
      {enablePriceGridColumnDynamics ? dialogs : null}
      {jsonModal}
    </>
  );
}
